import { formatDataverseRecordKey } from '../lib/dataverseRecordId';
import { normalizeMobileNumber } from '../config/keyRequest';
import {
  getCustomerById,
  getCustomerLoginTimeoutHours
} from '../config/customers';
import { getResolvedCustomerId } from '../config/environment';
import { getCustomerFromCurrentHostname } from '../lib/customerFromHostname';
import { getSelectedCustomer } from '../lib/selectedCustomer';
import {
  mobileEqualsFilter,
  odataFilterParam
} from '../lib/odataQuery';
import { ensureManagedUserInUnloc, listManagedUsers } from './unlocKeyService';

const PHONE_SELECT =
  'oc_telefonid,oc_name,oc_mobilnummer,oc_blaalys,oc_lagrenokler,' +
  'oc_is_logged_in,oc_last_logon,oc_last_logoff,oc_last_device_id,oc_last_user_id,' +
  'oc_last_email,oc_last_name,statecode,statuscode';

function resolveActiveCustomer() {
  return (
    getCustomerFromCurrentHostname() ||
    getSelectedCustomer() ||
    getCustomerById(getResolvedCustomerId()) ||
    null
  );
}

/**
 * Effective login: Dataverse flag AND last_logon within customer loginTimeoutHours.
 */
export function isPhoneEffectivelyLoggedIn(phone, customer = resolveActiveCustomer()) {
  const flagged =
    typeof phone?.isLoggedIn === 'boolean'
      ? phone.isLoggedIn
      : Boolean(phone?.oc_is_logged_in);
  if (!flagged) {
    return false;
  }

  const lastLogon = phone.lastLogon || phone.oc_last_logon || null;
  if (!lastLogon) {
    return false;
  }

  const timeoutHours = getCustomerLoginTimeoutHours(customer);
  if (!timeoutHours) {
    return true;
  }

  const logonAt = new Date(lastLogon).getTime();
  if (Number.isNaN(logonAt)) {
    return false;
  }

  const ageMs = Date.now() - logonAt;
  return ageMs <= timeoutHours * 60 * 60 * 1000;
}

function mapPhone(record) {
  const base = {
    id: record.oc_telefonid,
    phoneId: record.oc_telefonid,
    name: record.oc_name,
    mobileNumber: record.oc_mobilnummer,
    blaalys: Boolean(record.oc_blaalys),
    lagreNokler: Boolean(record.oc_lagrenokler),
    isLoggedIn: Boolean(record.oc_is_logged_in),
    lastLogon: record.oc_last_logon || null,
    lastLogoff: record.oc_last_logoff || null,
    lastDeviceId: record.oc_last_device_id || '',
    lastUserId: record.oc_last_user_id || '',
    lastEmail: record.oc_last_email || '',
    lastName: record.oc_last_name || '',
    stateCode: record.statecode,
    statusCode: record.statuscode,
    label: `${record.oc_name} (${record.oc_mobilnummer})`
  };

  return {
    ...base,
    isLoggedIn: isPhoneEffectivelyLoggedIn(base)
  };
}

function mobileLookupCandidates(mobileNumber) {
  const normalized = normalizeMobileNumber(mobileNumber);
  if (!normalized) {
    return [];
  }

  const localDigits = normalized.slice(3);
  return [...new Set([
    normalized,
    `+47${localDigits}`,
    `47${localDigits}`,
    localDigits,
    `0047${localDigits}`
  ])];
}

function isDuplicateMobileError(error) {
  const message = String(error?.message || '');
  return message.includes('412') && message.includes('Mobilnummer');
}

/**
 * Prefer Unloc managed-user firstName/lastName (project fields) over
 * appUserProfile.name, which is often a short consumer-app nickname.
 */
export function buildNameFromManagedUser(managedUser) {
  const firstName = managedUser?.firstName?.trim() || '';
  const lastName = managedUser?.lastName?.trim() || '';
  const combined = `${firstName} ${lastName}`.trim();
  if (combined) {
    return combined;
  }

  const profileName = managedUser?.appUserProfile?.name?.trim();
  if (profileName) {
    return profileName;
  }

  return managedUser?.appUserId || 'Unloc-bruker';
}

function buildPhoneIndex(phones) {
  const byMobile = new Map();

  for (const phone of phones) {
    for (const candidate of mobileLookupCandidates(phone.mobileNumber)) {
      byMobile.set(candidate, phone);
    }
  }

  return byMobile;
}

function lookupPhoneInIndex(phoneIndex, mobileNumber) {
  const normalized = normalizeMobileNumber(mobileNumber);
  if (!normalized) {
    return null;
  }

  return phoneIndex.get(normalized) || phoneIndex.get(mobileNumber) || null;
}

export function createPhoneService(dataverseApiCall) {
  const loadPhone = async (phoneId) => {
    const response = await dataverseApiCall(
      `/oc_telefons(${formatDataverseRecordKey(phoneId)})?$select=${PHONE_SELECT}`
    );
    return mapPhone(response);
  };

  const findByMobile = async (mobileNumber, { includeInactive = false } = {}) => {
    for (const candidate of mobileLookupCandidates(mobileNumber)) {
      const filter = odataFilterParam(
        mobileEqualsFilter(
          'oc_mobilnummer',
          candidate,
          includeInactive ? '' : 'statecode eq 0'
        )
      );
      const response = await dataverseApiCall(
        `/oc_telefons?$select=${PHONE_SELECT}&$filter=${filter}&$top=1`
      );
      const record = response.value?.[0];
      if (record) {
        return mapPhone(record);
      }
    }

    return null;
  };

  const createPhoneRecord = async (payload) => {
    const response = await dataverseApiCall('/oc_telefons', {
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify(payload)
    });
    return mapPhone(response);
  };

  const loadAllPhones = async () => {
    const response = await dataverseApiCall(`/oc_telefons?$select=${PHONE_SELECT}`);
    return (response.value || []).map(mapPhone);
  };

  const reactivatePhone = async (phoneId, payload) => {
    await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(phoneId)})`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...payload,
        statecode: 0,
        statuscode: 1
      })
    });
    return loadPhone(phoneId);
  };

  const upsertPhoneRecord = async ({ name, mobileNumber, blaalys, lagreNokler }) => {
    const normalized = normalizeMobileNumber(mobileNumber);
    if (!normalized) {
      throw new Error('Ugyldig mobilnummer. Bruk norsk format, f.eks. +4791750101.');
    }

    const payload = {
      oc_name: name.trim(),
      oc_mobilnummer: normalized,
      oc_blaalys: Boolean(blaalys),
      oc_lagrenokler: Boolean(lagreNokler)
    };

    const existing = await findByMobile(normalized, { includeInactive: true });
    if (existing) {
      if (existing.stateCode === 0) {
        throw new Error(
          `Mobilnummeret ${normalized} er allerede registrert som «${existing.name}».`
        );
      }

      return reactivatePhone(existing.phoneId, payload);
    }

    try {
      return await createPhoneRecord(payload);
    } catch (error) {
      if (!isDuplicateMobileError(error)) {
        throw error;
      }

      const duplicate = await findByMobile(normalized, { includeInactive: true });
      if (!duplicate) {
        throw error;
      }

      if (duplicate.stateCode === 0) {
        throw new Error(
          `Mobilnummeret ${normalized} er allerede registrert som «${duplicate.name}».`
        );
      }

      return reactivatePhone(duplicate.phoneId, payload);
    }
  };

  return {
    getAll: async ({ includeInactive = false } = {}) => {
      const filter = includeInactive ? '' : '&$filter=statecode eq 0';
      const response = await dataverseApiCall(
        `/oc_telefons?$select=${PHONE_SELECT}&$orderby=oc_name asc${filter}`
      );
      return (response.value || []).map(mapPhone);
    },

    findByMobile,

    create: async (
      { name, mobileNumber, blaalys = false, lagreNokler = false },
      { syncUnloc = true } = {}
    ) => {
      const phone = await upsertPhoneRecord({ name, mobileNumber, blaalys, lagreNokler });

      if (syncUnloc) {
        await ensureManagedUserInUnloc({
          mobileNumber: phone.mobileNumber,
          name: phone.name
        });
      }

      return phone;
    },

    update: async (phoneId, { name, mobileNumber, blaalys, lagreNokler }, { syncUnloc = true } = {}) => {
      const payload = {};

      if (name !== undefined) {
        payload.oc_name = name.trim();
      }

      if (mobileNumber !== undefined) {
        const normalized = normalizeMobileNumber(mobileNumber);
        if (!normalized) {
          throw new Error('Ugyldig mobilnummer. Bruk norsk format, f.eks. +4791750101.');
        }

        const duplicate = await findByMobile(normalized, { includeInactive: true });
        if (duplicate && duplicate.phoneId !== phoneId) {
          throw new Error(
            `Mobilnummeret ${normalized} er allerede registrert som «${duplicate.name}».`
          );
        }

        payload.oc_mobilnummer = normalized;
      }

      if (blaalys !== undefined) {
        payload.oc_blaalys = Boolean(blaalys);
      }

      if (lagreNokler !== undefined) {
        payload.oc_lagrenokler = Boolean(lagreNokler);
      }

      if (Object.keys(payload).length) {
        await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(phoneId)})`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
      }

      const phone = await loadPhone(phoneId);

      if (syncUnloc && (name !== undefined || mobileNumber !== undefined)) {
        await ensureManagedUserInUnloc({
          mobileNumber: phone.mobileNumber,
          name: phone.name
        });
      }

      return phone;
    },

    deactivate: async (phoneId) => {
      await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(phoneId)})`, {
        method: 'PATCH',
        body: JSON.stringify({ statecode: 1, statuscode: 2 })
      });
    },

    importFromUnloc: async () => {
      const result = await listManagedUsers();
      if (!result) {
        throw new Error('Kunne ikke hente brukere fra Unloc.');
      }

      const phoneIndex = buildPhoneIndex(await loadAllPhones());
      const summary = {
        imported: 0,
        reactivated: 0,
        updated: 0,
        skipped: 0
      };

      for (const managedUser of result.managedUsers || []) {
        const mobile = normalizeMobileNumber(managedUser.appUserId);
        if (!mobile) {
          summary.skipped += 1;
          continue;
        }

        const displayName = buildNameFromManagedUser(managedUser);
        const existing = lookupPhoneInIndex(phoneIndex, mobile);

        if (!existing) {
          const payload = {
            oc_name: displayName,
            oc_mobilnummer: mobile,
            oc_blaalys: false,
            oc_lagrenokler: false
          };

          let saved;
          try {
            saved = await createPhoneRecord(payload);
            summary.imported += 1;
          } catch (error) {
            if (!isDuplicateMobileError(error)) {
              throw error;
            }

            const duplicate = await findByMobile(mobile, { includeInactive: true });
            if (!duplicate) {
              throw error;
            }

            if (duplicate.stateCode === 0) {
              saved = duplicate;
            } else {
              saved = await reactivatePhone(duplicate.phoneId, payload);
              summary.reactivated += 1;
            }
          }

          for (const candidate of mobileLookupCandidates(saved.mobileNumber)) {
            phoneIndex.set(candidate, saved);
          }
          continue;
        }

        if (existing.stateCode !== 0) {
          const reactivated = await reactivatePhone(existing.phoneId, {
            oc_name: displayName,
            oc_mobilnummer: mobile,
            oc_blaalys: existing.blaalys,
            oc_lagrenokler: existing.lagreNokler
          });
          for (const candidate of mobileLookupCandidates(reactivated.mobileNumber)) {
            phoneIndex.set(candidate, reactivated);
          }
          summary.reactivated += 1;
          continue;
        }

        if (displayName && displayName !== existing.name) {
          await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(existing.phoneId)})`, {
            method: 'PATCH',
            body: JSON.stringify({ oc_name: displayName })
          });
          const updated = { ...existing, name: displayName };
          for (const candidate of mobileLookupCandidates(updated.mobileNumber)) {
            phoneIndex.set(candidate, updated);
          }
          summary.updated += 1;
        }
      }

      return summary;
    },

    syncFromUnloc: async (phoneId) => {
      const phone = await loadPhone(phoneId);
      const result = await listManagedUsers();
      if (!result) {
        throw new Error('Kunne ikke hente brukere fra Unloc.');
      }

      const managedUser = (result.managedUsers || []).find(
        (user) => normalizeMobileNumber(user.appUserId) === phone.mobileNumber
      );

      if (!managedUser) {
        await ensureManagedUserInUnloc({
          mobileNumber: phone.mobileNumber,
          name: phone.name
        });
        return loadPhone(phoneId);
      }

      const displayName = buildNameFromManagedUser(managedUser);
      if (displayName && displayName !== phone.name) {
        await dataverseApiCall(`/oc_telefons(${formatDataverseRecordKey(phoneId)})`, {
          method: 'PATCH',
          body: JSON.stringify({ oc_name: displayName })
        });
      }

      return loadPhone(phoneId);
    }
  };
}
